import React, { useState, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, BookOpen, GraduationCap, Award, RefreshCw,
  Briefcase, Heart, DollarSign, Clock, Users, Home, Star, Target,
  CheckCircle, AlertCircle, ArrowRight, MapPin, Calendar, Lightbulb,
  Shield, TrendingUp, HelpCircle, Phone, Globe, Sparkles, Printer,
  BarChart3, Zap, Brain, Battery, UserCheck, Scale, Eye, GitCompare
} from 'lucide-react';

// Import life paths data
import lifePathsData from '../data/life-paths-jamaica.json';

const BackButton = ({ onClick, label = "Back" }) => (
  <button onClick={onClick} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// Icon mapping
const iconMap = {
  BookOpen, GraduationCap, Award, RefreshCw, Briefcase, Heart, DollarSign,
  Clock, Users, Home, Star, Target, CheckCircle, AlertCircle, ArrowRight
};

// Progress bar with sections
const ProgressBar = ({ current, total, sectionName }) => (
  <div className="mb-6">
    <div className="flex justify-between items-center text-sm mb-2">
      <span className="text-purple-700 font-medium">{sectionName}</span>
      <span className="text-gray-500">Step {current} of {total}</span>
    </div>
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-500"
        style={{ width: `${(current / total) * 100}%` }}
      />
    </div>
  </div>
);

// Section header component
const SectionHeader = ({ icon: Icon, title, description, color = "purple" }) => (
  <div className={`bg-gradient-to-r from-${color}-500 to-${color}-600 rounded-xl p-4 sm:p-6 text-white mb-4 sm:mb-6`}>
    <div className="flex items-center gap-2 sm:gap-3 mb-2">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold">{title}</h2>
    </div>
    <p className="text-white/90 text-sm sm:text-base">{description}</p>
  </div>
);

// Rating scale component
const RatingOption = ({ question, options, value, onChange, help }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 mb-3 sm:mb-4">
    <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">{question}</h3>
    {help && <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{help}</p>}
    <div className="space-y-1.5 sm:space-y-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`w-full p-2.5 sm:p-3 rounded-lg text-left transition-all ${
            value === option.value
              ? 'bg-purple-100 border-2 border-purple-500'
              : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
          }`}
        >
          <div className="font-medium text-gray-800 text-sm sm:text-base">{option.label}</div>
          {option.desc && <div className="text-xs sm:text-sm text-gray-500">{option.desc}</div>}
        </button>
      ))}
    </div>
  </div>
);

export const LifePathSimulator = ({ setPage, goBack }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    // Basic situation
    current_level: null,
    academic_strength: null,
    family_support: null,
    financial_situation: null,
    responsibilities: null,
    goal: null,
    timeline: null,
    // Personal assessment
    resilience: null,
    self_discipline: null,
    support_network: null,
    energy_level: null,
    // Learning preferences
    learning_structure: null,
    study_time_preference: null,
    // Barriers
    biggest_barrier: null,
    past_attempts: null
  });
  const [results, setResults] = useState(null);
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [viewingMilestone, setViewingMilestone] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [comparedPathways, setComparedPathways] = useState([]);
  const resultsRef = useRef(null);

  // Define sections and steps
  const sections = [
    { name: "Welcome", steps: 1 },
    { name: "Your Situation", steps: 7 },
    { name: "Personal Strengths", steps: 4 },
    { name: "Learning Style", steps: 2 },
    { name: "Barriers & History", steps: 2 }
  ];

  const situationQuestions = lifePathsData.situation_questions;
  const personalQuestions = lifePathsData.personal_assessment_questions || [];
  const learningQuestions = lifePathsData.learning_preference_questions || [];
  const barrierQuestions = lifePathsData.barriers_assessment || [];

  const allQuestions = [
    ...situationQuestions,
    ...personalQuestions,
    ...learningQuestions,
    ...barrierQuestions
  ];

  const totalSteps = allQuestions.length + 1; // +1 for intro

  const getCurrentSection = () => {
    if (step === 0) return "Welcome";
    if (step <= 7) return "Your Situation";
    if (step <= 11) return "Personal Strengths";
    if (step <= 13) return "Learning Style";
    return "Barriers & History";
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      calculateResults();
    }
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const calculateResults = () => {
    const pathways = [];
    const {
      current_level, academic_strength, family_support, financial_situation,
      responsibilities, goal, timeline,
      resilience, self_discipline, support_network, energy_level,
      learning_structure, study_time_preference, biggest_barrier, past_attempts
    } = answers;

    // Calculate scores for each pathway
    const pathwayConfigs = [
      {
        pathway: lifePathsData.pathways.traditional_academic,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Academic strength matters most
          if (academic_strength === 'strong') { score += 30; reasons.push('Strong academic background'); }
          else if (academic_strength === 'average') { score += 15; }
          else { score -= 10; }

          // Support and finances
          if (family_support === 'full' || family_support === 'partial') { score += 20; reasons.push('Family support available'); }
          if (financial_situation === 'comfortable' || financial_situation === 'tight') { score += 15; }

          // Goals alignment
          if (goal === 'university') { score += 25; reasons.push('University is your goal'); }
          else if (goal === 'unsure') { score += 10; }

          // Timeline
          if (timeline === '3_4_years' || timeline === 'flexible') { score += 15; reasons.push('Timeline fits'); }
          else if (timeline === 'asap' || timeline === '1_2_years') { score -= 15; }

          // Personal factors
          if (self_discipline >= 4) { score += 10; reasons.push('Strong self-discipline'); }
          if (responsibilities === 'none') { score += 15; reasons.push('Can focus on studies'); }
          else if (responsibilities !== 'work_fulltime') { score += 5; }

          // Learning style
          if (learning_structure === 'structured') { score += 10; reasons.push('Prefer structured learning'); }

          return { score: Math.max(0, score), reasons };
        }
      },
      {
        pathway: lifePathsData.pathways.heart_tvet,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Quick employment need
          if (timeline === 'asap' || timeline === '1_2_years') { score += 25; reasons.push('Fast track to employment'); }

          // Goals
          if (goal === 'skill' || goal === 'good_job') { score += 25; reasons.push('Matches your career goal'); }
          else if (goal === 'unsure') { score += 15; }

          // Financial situation - HEART is free
          if (financial_situation === 'struggling' || financial_situation === 'path') {
            score += 25; reasons.push('Free/subsidized training');
          }
          else if (financial_situation === 'tight') { score += 15; }

          // Support
          if (family_support === 'minimal' || family_support === 'none') { score += 15; reasons.push('Designed for self-starters'); }

          // Academic strength - TVET welcomes all
          if (academic_strength === 'struggling' || academic_strength === 'average') {
            score += 10; reasons.push('Practical focus, not exam-heavy');
          }

          // Learning style
          if (learning_structure === 'hands_on') { score += 15; reasons.push('Hands-on learning style'); }

          // Barriers
          if (biggest_barrier === 'money') { score += 15; reasons.push('No fees barrier'); }

          return { score: Math.max(0, score), reasons };
        }
      },
      {
        pathway: lifePathsData.pathways.working_student,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Already working or need to work
          if (responsibilities === 'work_fulltime' || responsibilities === 'work_parttime') {
            score += 30; reasons.push('Already balancing work');
          }

          // Support situation
          if (family_support === 'minimal' || family_support === 'none') {
            score += 20; reasons.push('Need to support yourself');
          }

          // Financial
          if (financial_situation === 'struggling' || financial_situation === 'tight') {
            score += 15; reasons.push('Earn while you learn');
          }

          // Time barrier
          if (biggest_barrier === 'time') { score += 20; reasons.push('Flexible schedule options'); }

          // Energy and discipline
          if (self_discipline >= 3) { score += 10; }
          if (energy_level >= 3) { score += 10; reasons.push('Have energy for evening study'); }

          // Learning preference
          if (learning_structure === 'self_paced' || learning_structure === 'semi_structured') {
            score += 10; reasons.push('Fits flexible learning style');
          }

          // Study time
          if (study_time_preference === 'evening' || study_time_preference === 'weekends') {
            score += 10; reasons.push('Evening/weekend study works for you');
          }

          return { score: Math.max(0, score), reasons };
        }
      },
      {
        pathway: lifePathsData.pathways.second_chance,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Current status
          if (current_level === 'left_school' || current_level === 'working') {
            score += 25; reasons.push('Pathway for those returning to education');
          }

          // Academic history
          if (academic_strength === 'returning' || academic_strength === 'struggling') {
            score += 25; reasons.push('Fresh start opportunity');
          }

          // Past attempts
          if (past_attempts === 'once' || past_attempts === 'multiple') {
            score += 20; reasons.push('Supportive for those who tried before');
          }

          // Confidence barrier
          if (biggest_barrier === 'confidence' || biggest_barrier === 'grades') {
            score += 15; reasons.push('Build confidence step by step');
          }

          // Resilience
          if (resilience >= 3) { score += 10; reasons.push('Your resilience will help'); }

          return { score: Math.max(0, score), reasons };
        }
      },
      {
        pathway: lifePathsData.pathways.entrepreneurship,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Goal
          if (goal === 'business') { score += 35; reasons.push('Entrepreneurship is your goal'); }

          // Self-discipline and resilience important for business
          if (self_discipline >= 4) { score += 15; reasons.push('Strong self-discipline'); }
          if (resilience >= 4) { score += 15; reasons.push('Resilience for business challenges'); }

          // Timeline
          if (timeline === 'asap' || timeline === '1_2_years') { score += 10; }

          // Learning style
          if (learning_structure === 'hands_on' || learning_structure === 'self_paced') {
            score += 10; reasons.push('Entrepreneurial learning style');
          }

          // Current work
          if (responsibilities === 'work_fulltime') {
            score += 5; reasons.push('Can start as side business');
          }

          return { score: Math.max(0, score), reasons };
        }
      },
      {
        pathway: lifePathsData.pathways.parent_caregiver,
        calculateScore: () => {
          let score = 0;
          let reasons = [];

          // Primary criteria
          if (responsibilities === 'parent' || responsibilities === 'family_care') {
            score += 40; reasons.push('Designed for parent/caregivers');
          }

          // Energy consideration
          if (energy_level <= 3) { score += 10; reasons.push('Accounts for caregiver fatigue'); }

          // Time barrier
          if (biggest_barrier === 'time') { score += 15; reasons.push('Flexible scheduling'); }

          // Support network
          if (support_network >= 3) { score += 10; reasons.push('Support network can help'); }

          // Learning preference
          if (learning_structure === 'self_paced' || study_time_preference === 'evening' || study_time_preference === 'weekends') {
            score += 10; reasons.push('Flexible learning options');
          }

          return { score: Math.max(0, score), reasons };
        }
      }
    ];

    // Calculate all pathway scores
    pathwayConfigs.forEach(config => {
      const { score, reasons } = config.calculateScore();
      if (score > 0) {
        pathways.push({
          ...config.pathway,
          matchScore: score,
          matchPercentage: Math.min(100, Math.round((score / 100) * 100)),
          matchReasons: reasons
        });
      }
    });

    // Sort by score and ensure at least 3 options
    const sortedPathways = pathways.sort((a, b) => b.matchScore - a.matchScore);

    // Add paths if we have fewer than 3
    if (sortedPathways.length < 3) {
      if (!sortedPathways.find(p => p.id === 'heart_tvet')) {
        sortedPathways.push({
          ...lifePathsData.pathways.heart_tvet,
          matchScore: 40,
          matchPercentage: 40,
          matchReasons: ['Always a solid option for practical skills']
        });
      }
      if (!sortedPathways.find(p => p.id === 'working_student')) {
        sortedPathways.push({
          ...lifePathsData.pathways.working_student,
          matchScore: 35,
          matchPercentage: 35,
          matchReasons: ['Flexible option for anyone']
        });
      }
    }

    setResults(sortedPathways.slice(0, 5));
  };

  const restartSimulator = () => {
    setStep(0);
    setAnswers({
      current_level: null,
      academic_strength: null,
      family_support: null,
      financial_situation: null,
      responsibilities: null,
      goal: null,
      timeline: null,
      resilience: null,
      self_discipline: null,
      support_network: null,
      energy_level: null,
      learning_structure: null,
      study_time_preference: null,
      biggest_barrier: null,
      past_attempts: null
    });
    setResults(null);
    setSelectedPathway(null);
    setCompareMode(false);
    setComparedPathways([]);
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleCompare = (pathway) => {
    if (comparedPathways.find(p => p.id === pathway.id)) {
      setComparedPathways(comparedPathways.filter(p => p.id !== pathway.id));
    } else if (comparedPathways.length < 3) {
      setComparedPathways([...comparedPathways, pathway]);
    }
  };

  // Intro screen
  const IntroStep = () => (
    <div className="text-center max-w-2xl mx-auto px-2 sm:px-0">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
        <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-black text-gray-800 mb-3 sm:mb-4">
        Life Path Simulator
      </h1>
      <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
        Every successful person's journey had twists and turns. Let's explore what path might work for you,
        based on where you are right now - not where you think you "should" be.
      </p>

      <div className="bg-purple-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 text-left">
        <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
          <Lightbulb className="w-5 h-5 flex-shrink-0" />
          What we'll explore together:
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-purple-700 text-sm sm:text-base">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Your Situation</strong> - Where you are now</span>
            </div>
            <div className="flex items-start gap-2 text-purple-700 text-sm sm:text-base">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Personal Strengths</strong> - Your resilience & discipline</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-purple-700 text-sm sm:text-base">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Learning Style</strong> - How you learn best</span>
            </div>
            <div className="flex items-start gap-2 text-purple-700 text-sm sm:text-base">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Barriers</strong> - What's in your way</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4 mb-6 sm:mb-8">
        <p className="text-amber-800 text-xs sm:text-sm">
          <strong>Remember:</strong> This is a starting point for exploration, not a fixed plan.
          Your journey will be unique. What matters is taking the next step.
        </p>
      </div>

      <button
        onClick={nextStep}
        className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-bold text-base sm:text-lg hover:shadow-lg transition-all"
      >
        Let's Explore Your Path
      </button>
    </div>
  );

  // Question step
  const QuestionStep = ({ question }) => (
    <div className="max-w-2xl mx-auto px-1 sm:px-0">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">{question.question}</h2>
      {question.help && (
        <p className="text-gray-500 mb-4 sm:mb-6 italic text-sm sm:text-base">{question.help}</p>
      )}

      <div className="space-y-2 sm:space-y-3">
        {question.options.map(option => {
          const isSelected = answers[question.id] === option.value;
          return (
            <button
              key={option.value}
              onClick={() => handleAnswer(question.id, option.value)}
              className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300 bg-white'
              }`}
            >
              <div className="font-semibold text-gray-800 text-sm sm:text-base">{option.label}</div>
              {option.desc && (
                <div className="text-xs sm:text-sm text-gray-500 mt-1">{option.desc}</div>
              )}
              {option.solution_hint && isSelected && (
                <div className="text-xs sm:text-sm text-purple-600 mt-2 flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {option.solution_hint}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Comparison view
  const ComparisonView = () => (
    <div className="space-y-4 sm:space-y-6">
      <BackButton onClick={() => setCompareMode(false)} label="Back to Results" />

      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 text-center">Compare Pathways</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {comparedPathways.map((pathway, index) => (
          <div key={pathway.id} className="bg-white rounded-xl shadow-sm border p-3 sm:p-4">
            <div className={`text-center p-2 sm:p-3 rounded-lg mb-3 sm:mb-4 text-white ${
              index === 0 ? 'bg-purple-600' : index === 1 ? 'bg-indigo-600' : 'bg-blue-600'
            }`}>
              <h3 className="font-bold text-sm sm:text-base">{pathway.name}</h3>
              <p className="text-xs sm:text-sm opacity-90">{pathway.matchPercentage}% Match</p>
            </div>

            <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm">
              <div>
                <strong className="text-gray-600">Duration:</strong>
                <p>{pathway.total_duration}</p>
              </div>
              <div>
                <strong className="text-gray-600">Cost:</strong>
                <p>{pathway.total_cost_estimate?.typical || pathway.total_cost_estimate?.with_support || 'Varies'}</p>
              </div>
              <div>
                <strong className="text-gray-600">Best For:</strong>
                <ul className="mt-1">
                  {pathway.best_for?.slice(0, 3).map((item, i) => (
                    <li key={i} className="text-gray-700">• {item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <strong className="text-gray-600">Why It Matches You:</strong>
                <ul className="mt-1">
                  {pathway.matchReasons?.slice(0, 3).map((reason, i) => (
                    <li key={i} className="text-purple-600">• {reason}</li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              onClick={() => { setCompareMode(false); setSelectedPathway(pathway); }}
              className="w-full mt-3 sm:mt-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
            >
              View Full Details
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  // Results - pathway selection
  const ResultsStep = () => (
    <div className="max-w-3xl mx-auto space-y-4 sm:space-y-6" ref={resultsRef}>
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Your Recommended Paths</h2>
        <p className="text-sm sm:text-base text-gray-600 mt-2 px-2">Based on your situation, strengths, and preferences, here are pathways that could work for you.</p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4 sm:mb-6 print:hidden">
        <button
          onClick={handlePrint}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" />
          <span className="hidden xs:inline">Print</span>
        </button>
        {comparedPathways.length >= 2 && (
          <button
            onClick={() => setCompareMode(true)}
            className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm bg-purple-100 text-purple-700 border border-purple-300 rounded-lg hover:bg-purple-200"
          >
            <GitCompare className="w-4 h-4" />
            Compare ({comparedPathways.length})
          </button>
        )}
        <button
          onClick={restartSimulator}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Start Over</span>
        </button>
      </div>

      {/* Compare instruction */}
      <p className="text-xs sm:text-sm text-center text-gray-500 print:hidden">
        Select 2-3 pathways to compare them side by side
      </p>

      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
        {results?.map((pathway, index) => (
          <div
            key={pathway.id}
            className={`bg-white rounded-xl p-3 sm:p-5 shadow-sm border-2 transition-all ${
              comparedPathways.find(p => p.id === pathway.id)
                ? 'border-purple-500 bg-purple-50'
                : index === 0 ? 'border-purple-300' : 'border-gray-100'
            }`}
          >
            <div className="flex items-start gap-2 sm:gap-4">
              {/* Compare checkbox */}
              <button
                onClick={() => toggleCompare(pathway)}
                className={`w-5 h-5 sm:w-6 sm:h-6 rounded border-2 flex-shrink-0 mt-1 flex items-center justify-center print:hidden ${
                  comparedPathways.find(p => p.id === pathway.id)
                    ? 'bg-purple-600 border-purple-600 text-white'
                    : 'border-gray-300 hover:border-purple-400'
                }`}
              >
                {comparedPathways.find(p => p.id === pathway.id) && (
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
              </button>

              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex flex-col items-center justify-center text-white font-bold flex-shrink-0 ${
                index === 0 ? 'bg-gradient-to-br from-purple-500 to-indigo-600' :
                index === 1 ? 'bg-gradient-to-br from-brand-500 to-brand-600' :
                index === 2 ? 'bg-gradient-to-br from-blue-500 to-cyan-600' :
                'bg-gradient-to-br from-gray-500 to-gray-600'
              }`}>
                <span className="text-xs">#{index + 1}</span>
                <span className="text-base sm:text-lg font-black">{pathway.matchPercentage}%</span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                  <h3 className="font-bold text-gray-800 text-base sm:text-lg leading-tight">{pathway.name}</h3>
                  {index === 0 && (
                    <span className="text-xs bg-purple-100 text-purple-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                      Best Match
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-purple-600 font-medium">{pathway.tagline}</p>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2 sm:line-clamp-none">{pathway.description}</p>

                {/* Match reasons */}
                <div className="flex flex-wrap gap-1 sm:gap-2 mt-2 sm:mt-3">
                  {pathway.matchReasons?.slice(0, 2).map((reason, i) => (
                    <span key={i} className="text-xs bg-brand-100 text-brand-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                      {reason}
                    </span>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3 mt-2 sm:mt-3 text-xs sm:text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    {pathway.total_duration}
                  </span>
                  {pathway.total_cost_estimate && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3 h-3 sm:w-4 sm:h-4" />
                      {pathway.total_cost_estimate.typical || 'Varies'}
                    </span>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedPathway(pathway)}
                className="flex items-center gap-1 text-purple-600 hover:text-purple-800 font-medium text-sm hidden sm:flex"
              >
                Details <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            {/* Mobile details button */}
            <button
              onClick={() => setSelectedPathway(pathway)}
              className="sm:hidden w-full mt-3 py-2 text-purple-600 hover:text-purple-800 font-medium text-sm border border-purple-200 rounded-lg"
            >
              View Details
            </button>
          </div>
        ))}
      </div>

      {/* Your Profile Summary */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 text-sm sm:text-base">Your Profile Summary</h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div>
            <strong className="text-gray-600">Goal:</strong>
            <p className="text-gray-800">
              {answers.goal === 'university' ? 'Go to university' :
               answers.goal === 'good_job' ? 'Get a good-paying job' :
               answers.goal === 'skill' ? 'Learn a skill/trade' :
               answers.goal === 'business' ? 'Start own business' : 'Still exploring'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Timeline:</strong>
            <p className="text-gray-800">
              {answers.timeline === 'asap' ? 'As soon as possible' :
               answers.timeline === '1_2_years' ? '1-2 years' :
               answers.timeline === '3_4_years' ? '3-4 years' : 'Flexible'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Biggest Barrier:</strong>
            <p className="text-gray-800">
              {answers.biggest_barrier === 'money' ? 'Financial' :
               answers.biggest_barrier === 'time' ? 'Time constraints' :
               answers.biggest_barrier === 'grades' ? 'Past grades' :
               answers.biggest_barrier === 'confidence' ? 'Confidence' :
               answers.biggest_barrier === 'location' ? 'Location' : 'None identified'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Learning Style:</strong>
            <p className="text-gray-800">
              {answers.learning_structure === 'structured' ? 'Structured classroom' :
               answers.learning_structure === 'hands_on' ? 'Hands-on practical' :
               answers.learning_structure === 'self_paced' ? 'Self-paced online' :
               answers.learning_structure === 'mixed' ? 'Mix of approaches' : 'Flexible'}
            </p>
          </div>
        </div>
      </div>

      {/* Encouragement */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 sm:p-6 border border-purple-100">
        <h3 className="font-bold text-purple-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
          <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
          Remember
        </h3>
        <p className="text-purple-700 text-sm sm:text-base">
          Success isn't one road. Many people change paths multiple times. The important thing is to
          start somewhere and keep moving forward. Every step counts.
        </p>
      </div>
    </div>
  );

  // Detailed pathway view (keeping the existing detailed view but enhanced)
  const PathwayDetailView = ({ pathway }) => (
    <div className="space-y-4 sm:space-y-6">
      <BackButton onClick={() => setSelectedPathway(null)} label="Back to Pathways" />

      {/* Header with match info */}
      <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full">{pathway.tagline}</span>
          <span className="text-xs sm:text-sm bg-white/30 px-2 sm:px-3 py-1 rounded-full font-bold">
            {pathway.matchPercentage}% Match
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black mt-2 sm:mt-3">{pathway.name}</h1>
        <p className="text-purple-100 mt-2 text-sm sm:text-base">{pathway.description}</p>
        {pathway.total_duration && (
          <div className="mt-3 sm:mt-4 flex items-center gap-2 text-purple-200 text-sm sm:text-base">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>Typical duration: {pathway.total_duration}</span>
          </div>
        )}
      </div>

      {/* Why this matches you */}
      <div className="bg-brand-50 rounded-xl border border-brand-200 p-4 sm:p-5">
        <h3 className="font-bold text-brand-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          Why This Matches Your Situation
        </h3>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {pathway.matchReasons?.map((reason, i) => (
            <span key={i} className="bg-brand-100 text-brand-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
              {reason}
            </span>
          ))}
        </div>
      </div>

      {/* Best for */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
        <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
          <Target className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          This Path is Good For You If...
        </h3>
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {pathway.best_for?.map((item, i) => (
            <span key={i} className="bg-purple-100 text-purple-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* Visual Timeline */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
          Your Journey Step by Step
        </h3>

        {/* Visual timeline bar - hidden on mobile */}
        <div className="relative mb-4 sm:mb-6 hidden sm:block">
          <div className="h-2 bg-purple-100 rounded-full">
            <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full" style={{ width: '100%' }} />
          </div>
          <div className="flex justify-between mt-2">
            {pathway.milestones?.map((_, index) => (
              <div
                key={index}
                className="w-4 h-4 bg-purple-500 rounded-full border-2 border-white shadow -mt-5"
                style={{ marginLeft: index === 0 ? 0 : 'auto' }}
              />
            ))}
          </div>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {pathway.milestones?.map((milestone, index) => (
            <div key={index} className="relative">
              {/* Connector line - hidden on mobile */}
              {index < pathway.milestones.length - 1 && (
                <div className="absolute left-4 sm:left-5 top-10 sm:top-12 w-0.5 h-full bg-purple-200 hidden sm:block" />
              )}

              <button
                onClick={() => setViewingMilestone(viewingMilestone === index ? null : index)}
                className="w-full text-left"
              >
                <div className="flex items-start gap-2 sm:gap-4">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-xs sm:text-sm flex-shrink-0 ${
                    index === 0 ? 'bg-purple-600' : 'bg-purple-400'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1 bg-gray-50 rounded-xl p-3 sm:p-4 hover:bg-purple-50 transition-colors min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                      <h4 className="font-bold text-gray-800 text-sm sm:text-base">{milestone.stage}</h4>
                      <span className="text-xs sm:text-sm text-purple-600">{milestone.duration}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">{milestone.what_happens}</p>

                    {/* Expanded content */}
                    {viewingMilestone === index && (
                      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200 space-y-2 sm:space-y-3">
                        {milestone.target && (
                          <div>
                            <span className="text-xs font-bold text-gray-500">TARGET:</span>
                            <p className="text-xs sm:text-sm text-gray-700">{milestone.target}</p>
                          </div>
                        )}
                        {milestone.cost && (
                          <div>
                            <span className="text-xs font-bold text-gray-500">COST:</span>
                            <p className="text-xs sm:text-sm text-gray-700">{milestone.cost}</p>
                          </div>
                        )}
                        {milestone.financial_aid && (
                          <div>
                            <span className="text-xs font-bold text-brand-600">FINANCIAL HELP:</span>
                            <ul className="text-xs sm:text-sm text-gray-700 mt-1">
                              {milestone.financial_aid.map((aid, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-brand-500 flex-shrink-0 mt-0.5" />
                                  {aid}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {milestone.tips && (
                          <div>
                            <span className="text-xs font-bold text-purple-600">TIPS:</span>
                            <ul className="text-xs sm:text-sm text-gray-700 mt-1">
                              {milestone.tips.map((tip, i) => (
                                <li key={i} className="flex items-start gap-2">
                                  <Lightbulb className="w-3 h-3 sm:w-4 sm:h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {milestone.options && (
                          <div>
                            <span className="text-xs font-bold text-gray-500">OPTIONS:</span>
                            <div className="grid gap-2 mt-2">
                              {milestone.options.map((opt, i) => (
                                <div key={i} className="bg-white rounded-lg p-2 sm:p-3 border border-gray-200">
                                  <div className="font-medium text-gray-800 text-xs sm:text-sm">{opt.name || opt}</div>
                                  {opt.description && <p className="text-xs text-gray-500">{opt.description}</p>}
                                  {opt.cost && <p className="text-xs text-brand-600 mt-1">Cost: {opt.cost}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="text-xs text-purple-600 mt-2">
                      {viewingMilestone === index ? 'Click to collapse' : 'Click to expand details'}
                    </p>
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Common Setbacks & Recovery */}
      {pathway.common_setbacks && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 sm:p-5">
          <h3 className="font-bold text-amber-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            When Things Don't Go As Planned
          </h3>
          <p className="text-amber-700 text-xs sm:text-sm mb-3 sm:mb-4">
            Setbacks are normal. Here's how to recover from common ones:
          </p>
          <div className="space-y-3 sm:space-y-4">
            {pathway.common_setbacks.map((setback, i) => (
              <div key={i} className="bg-white rounded-lg p-3 sm:p-4">
                <div className="font-semibold text-gray-800 text-sm sm:text-base">{setback.setback}</div>
                <div className="text-xs sm:text-sm text-brand-700 mt-1 sm:mt-2">
                  <strong>How to recover:</strong> {setback.recovery}
                </div>
                {setback.timeline_impact && (
                  <div className="text-xs text-gray-500 mt-1">
                    Timeline impact: {setback.timeline_impact}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cost Summary */}
      {pathway.total_cost_estimate && (
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
          <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
            <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            Cost Overview
          </h3>
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            {Object.entries(pathway.total_cost_estimate).map(([key, value]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-2 sm:p-3">
                <div className="text-xs text-gray-500 capitalize">{key.replace('_', ' ')}</div>
                <div className="font-semibold text-gray-800 text-xs sm:text-base">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Encouragement / Real Talk */}
      {(pathway.encouragement || pathway.real_talk) && (
        <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl p-4 sm:p-5">
          <h3 className="font-bold text-purple-800 mb-2 text-sm sm:text-base">Real Talk</h3>
          <p className="text-purple-700 text-sm sm:text-base">{pathway.encouragement || pathway.real_talk}</p>
        </div>
      )}

      {/* Financial Resources */}
      <div className="bg-brand-50 rounded-xl border border-brand-200 p-4 sm:p-5">
        <h3 className="font-bold text-brand-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
          Financial Help Available
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {lifePathsData.financial_resources.government.slice(0, 3).map((resource, i) => (
            <div key={i} className="bg-white rounded-lg p-3 sm:p-4">
              <div className="font-bold text-gray-800 text-sm sm:text-base">{resource.name}</div>
              <div className="text-xs text-brand-600">{resource.full_name || ''}</div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 sm:mt-2">{resource.what}</p>
              {resource.amount && (
                <p className="text-xs sm:text-sm text-brand-700 mt-1"><strong>Amount:</strong> {resource.amount}</p>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={() => setPage('funding')}
          className="mt-3 sm:mt-4 w-full py-2.5 sm:py-3 bg-brand-600 text-white rounded-lg font-semibold hover:bg-brand-700 text-sm sm:text-base"
        >
          Explore All Funding Options
        </button>
      </div>

      {/* Success Stories */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-5">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <Star className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />
          Real Jamaican Stories
        </h3>
        <div className="space-y-3 sm:space-y-4">
          {Object.values(lifePathsData.timeline_examples).map((story, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 sm:p-4">
              <div className="font-bold text-gray-800 text-sm sm:text-base">{story.name}</div>
              <p className="text-xs sm:text-sm text-gray-600">{story.start}</p>
              <p className="text-xs sm:text-sm text-purple-600 mt-1 sm:mt-2 italic">"{story.message}"</p>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 print:hidden">
        <button
          onClick={() => setPage('career-path')}
          className="flex-1 py-2.5 sm:py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 text-sm sm:text-base"
        >
          Explore Careers
        </button>
        <button
          onClick={() => setPage('schools')}
          className="flex-1 py-2.5 sm:py-3 bg-white border-2 border-purple-600 text-purple-600 rounded-xl font-semibold hover:bg-purple-50 text-sm sm:text-base"
        >
          Find Schools
        </button>
      </div>

      {/* Need Help */}
      <div className="bg-gray-50 rounded-xl p-4 sm:p-5 text-center">
        <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">Need Someone to Talk To?</h3>
        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
          It's okay to need guidance. Here are people who can help:
        </p>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
          <span className="text-xs sm:text-sm bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border">School Guidance Counsellor</span>
          <span className="text-xs sm:text-sm bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border">HEART Career Guidance</span>
          <span className="text-xs sm:text-sm bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border">Church/Community Leaders</span>
        </div>
      </div>
    </div>
  );

  // Main render
  if (compareMode && comparedPathways.length >= 2) {
    return (
      <div className="w-full">
        <ComparisonView />
      </div>
    );
  }

  if (selectedPathway) {
    return (
      <div className="w-full">
        <PathwayDetailView pathway={selectedPathway} />
      </div>
    );
  }

  // Show results after all questions
  if (results) {
    return (
      <div className="w-full">
        <BackButton onClick={goBack} />
        <ResultsStep />
      </div>
    );
  }

  // Get current question
  const currentQuestionIndex = step - 1;
  const currentQuestion = allQuestions[currentQuestionIndex];
  const isLastQuestion = step === allQuestions.length;
  const hasAnswer = currentQuestion && answers[currentQuestion.id] != null;

  // Show intro or questions
  return (
    <div className="w-full">
      <BackButton onClick={step === 0 ? goBack : prevStep} label={step === 0 ? "Back" : "Previous"} />

      {step > 0 && <ProgressBar current={step} total={allQuestions.length} sectionName={getCurrentSection()} />}

      {step === 0 ? (
        <IntroStep />
      ) : (
        <>
          <QuestionStep question={currentQuestion} />
          <div className="flex justify-between mt-6 sm:mt-8 max-w-2xl mx-auto px-1 sm:px-0">
            <button
              onClick={prevStep}
              className="px-4 sm:px-6 py-2.5 sm:py-3 text-gray-600 hover:text-gray-800 font-medium text-sm sm:text-base"
            >
              Previous
            </button>
            <button
              onClick={() => {
                if (isLastQuestion) {
                  calculateResults();
                } else {
                  nextStep();
                }
              }}
              disabled={!hasAnswer}
              className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base ${
                hasAnswer
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isLastQuestion ? 'See My Paths' : 'Continue'}
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LifePathSimulator;
